import React, { useMemo } from 'react';
import { useIntl } from 'react-intl';
import PropTypes from 'prop-types';

import PageHelmet from '@/components/Base/PageHelmet';
import TimestampComponent from '@/components/Base/Timestamp/Timestamp';
import ResponsiveMenuWrapper from '@/components/Pages/CompanyPage/ResponsiveMenuWrapper';
import ProductsFiltersForm from '@/components/Pages/Home/ProductsFiltersForm';
import { useGetProductsWithCompany, useSeoMeta } from '@/hooks';
import {
  convertTermMonthsToDays,
  getFormattedShortYearDate,
  getMinusHours,
  getOrganizationUrl,
  getProductPageAbsolutUrl,
  getRateDisplay,
  getReviewPageUrl,
} from '@/utils';

import {
  AdvertiserNoteWrapper,
  Banner,
  FiltersFormWrapper,
  Icon,
  Img,
  LinkToReview,
  Logo,
  LogoWrapper,
  MainInfoWrapper,
  Name,
  Wrapper,
} from './CompanyPageHeader.styles';

function CompanyPageHeader({
  id = null,
  banner = '',
  logoUrl: logo = '',
  menu = [],
  slug = '',
  companyName: name = '',
  companyNameP = '',
}) {
  const { locale, formatMessage } = useIntl();

  if (!id) return null;

  const { metaTitle, metaDescription, pageTitle } = useSeoMeta({
    title: name,
    companyName: name,
    companyNameP,
  });

  const { products } = useGetProductsWithCompany({
    locale,
  });

  const pageUrl = `${process.env.APP_DEPLOY_URL}${getOrganizationUrl(slug)}`;

  const schema = useMemo(
    () => [
      {
        type: 'application/ld+json',
        innerHTML: JSON.stringify({
          '@context': 'https://schema.org',
          '@graph': [
            {
              '@type': 'BankOrCreditUnion',
              '@id': `${pageUrl}#bank`,
              name: pageTitle,
              url: pageUrl,
              logo,
              address: {
                '@type': 'PostalAddress',
                addressCountry: 'UA',
              },
            },
            {
              '@type': 'WebPage',
              name: metaTitle,
              url: pageUrl,
              description: metaDescription,
              publisher: {
                '@type': 'Organization',
                name: 'Minfin.com.ua',
                url: 'https://minfin.com.ua/',
              },
              mainEntity: {
                '@id': `${pageUrl}#loan`,
              },
            },
            {
              '@type': 'ItemList',
              '@id': `${pageUrl}#loan-list`,
              name: formatMessage(
                { id: 'shema.companyPage.list' },
                { bank: name }
              ),
              itemListOrder: 'http://schema.org/ItemListOrderAscending',
              itemListElement: products
                ?.slice(0, 5)
                ?.map(
                  (
                    {
                      companyName,
                      termMonths = {},
                      loanRateYear,
                      baseCalculatedRate,
                      calculatedRateLimit,
                      periodicalRateCollection,
                      standardUird,
                      loanLimitNumber,
                      currency,
                      companySlug,
                      productName,
                      companyNameG,
                      productId,
                      slug: productSlug,
                    },
                    itemIndex
                  ) => {
                    const rateDisplay = getRateDisplay({
                      loanRateYear,
                      baseCalculatedRate,
                      calculatedRateLimit,
                      formatMessage,
                      periodicalRateCollection,
                      standardUird,
                    });
                    const productUrl = getProductPageAbsolutUrl({
                      org: companySlug,
                      id: productId,
                      slug: productSlug,
                      currency,
                      locale,
                    });

                    return {
                      '@type': 'ListItem',
                      position: itemIndex + 1,
                      item: {
                        '@type': 'LoanOrCredit',
                        '@id': `${pageUrl}#loan${itemIndex + 1}`,
                        name: formatMessage(
                          { id: 'productCard.title' },
                          { productName, companyNameG }
                        ),
                        description: formatMessage(
                          {
                            id: 'shema.companyPage.list.description',
                          },
                          { bank: companyName }
                        ),
                        provider: {
                          '@id': `${pageUrl}#bank`,
                        },
                        loanType: 'BusinessLoan',
                        amount: {
                          '@type': 'MonetaryAmount',
                          name: formatMessage({ id: 'shema.amount.name' }),
                          minValue: loanLimitNumber?.min,
                          maxValue: loanLimitNumber?.max,
                          currency,
                        },
                        interestRate: rateDisplay?.rate || 0,
                        loanTerm: {
                          '@type': 'QuantitativeValue',
                          ...convertTermMonthsToDays(termMonths),
                          unitText: 'days',
                        },
                        offers: {
                          '@type': 'Offer',
                          url: productUrl,
                          priceCurrency: currency,
                          price: formatMessage(
                            { id: 'shema.companyPage.list.rate' },
                            { rate: rateDisplay?.rate || 0 }
                          ),
                          availability: 'https://schema.org/InStock',
                          eligibleRegion: {
                            '@type': 'Country',
                            name: 'UA',
                          },
                        },
                      },
                    };
                  }
                ),
            },
          ],
        }),
      },
    ],
    [formatMessage, pageTitle, metaDescription, products, pageUrl]
  );

  const updatedDate = getFormattedShortYearDate(
    getMinusHours({ date: Date.now(), hours: 6 })
  );

  return (
    <Wrapper>
      <PageHelmet
        title={metaTitle}
        description={metaDescription}
        script={schema}
      />

      <Banner img={banner} />

      <MainInfoWrapper>
        <LogoWrapper>
          <Logo>
            <Img src={logo} />
          </Logo>

          <Name>{pageTitle}</Name>
        </LogoWrapper>

        <LinkToReview
          href={getReviewPageUrl({ locale, companySlug: slug })}
          rel="nofollow"
        >
          <Icon name="plus" />

          {formatMessage({ id: 'addReview' })}
        </LinkToReview>
      </MainInfoWrapper>

      {/* {!!menu && <div>menu</div>} */}

      {!!menu && <ResponsiveMenuWrapper menu={menu} />}

      <FiltersFormWrapper>
        <AdvertiserNoteWrapper>
          <TimestampComponent latestTimestamp={updatedDate} />
        </AdvertiserNoteWrapper>

        <ProductsFiltersForm isCompanyPage />
      </FiltersFormWrapper>
    </Wrapper>
  );
}

CompanyPageHeader.propTypes = {
  id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  banner: PropTypes.string,
  logoUrl: PropTypes.string,
  menu: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      title: PropTypes.string,
      url: PropTypes.string,
    })
  ),
  slug: PropTypes.string,
  companyName: PropTypes.string,
  companyNameP: PropTypes.string,
};

export default CompanyPageHeader;
